/**
 * A label, an optional hint, one control, and the error for it, wired together:
 *
 *   <Field label="Email" error={problem}>
 *     {(control) => <Input {...control} type="email" value={email} onChange={setEmail} />}
 *   </Field>
 *
 * The label is always visible and names the control. The hint, the error and the character count
 * reach a screen reader through `aria-describedby`, and an error sets `aria-invalid`. The error sits
 * directly under the control it is about, never at the bottom of the page, and is announced when
 * it appears, because it appears in answer to something the person did.
 *
 * A field is required unless it says `optional`, which adds "(optional)" to the label. Marking the
 * few optional fields rather than the many required ones is the GOV.UK pattern. Required is
 * `aria-required`, not the `required` attribute, so the browser's own message never stands in front
 * of the form's: a form checks its fields on submit and puts each error under its field.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import { useId } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import type { FieldControl } from "./controlStyle.ts";
import { WarningIcon } from "./icons.tsx";
import { textStyle } from "./textStyle.ts";
const styles = {
  label: {
    kMwMTN: "xm8xz4y",
    kHjlTd: "xj0a0fe",
    $$css: true
  },
  hint: {
    kogj98: "x1ghz6dp",
    kMwMTN: "x1lm7cez",
    kN2L0X: "x1fzhlzt",
    $$css: true
  },
  error: {
    k1xSpc: "x78zum5",
    kGNEyG: "x1cy8zhl",
    kOIVth: "xskm3c9",
    kogj98: "x1ghz6dp",
    kMwMTN: "x6ygkrl",
    k63SB2: "x1pd3egz",
    kHjlTd: "xj0a0fe",
    $$css: true
  },
  count: {
    kogj98: "x1ghz6dp",
    kMwMTN: "x1lm7cez",
    kcqcaj: "x1l1vl4p",
    $$css: true
  },
  over: {
    kMwMTN: "x6ygkrl",
    $$css: true
  }
};
export interface FieldProps {
  readonly label: string;
  readonly children: (control: FieldControl) => ReactNode;
  readonly hint?: ReactNode | undefined;
  /** What is wrong and how to fix it. Leave it out, or pass null, when nothing is. */
  readonly error?: string | null;
  /** Adds "(optional)" to the label and leaves the control not required. */
  readonly optional?: boolean;
  /** Shows "12 of 200" under the control, for a field with a length limit. */
  readonly counter?: {
    readonly length: number;
    readonly max: number;
  };
  /** The control's id, when something else must point at it. One is made otherwise. */
  readonly id?: string;
  readonly testId?: string;
}
export function Field({
  label,
  children,
  hint,
  error,
  optional = false,
  counter,
  id,
  testId
}: FieldProps) {
  const made = useId();
  const control = id ?? made;
  const hintId = `${control}-hint`;
  const errorId = `${control}-error`;
  const countId = `${control}-count`;
  const failed = error !== undefined && error !== null && error !== "";
  const describedBy = [hint === undefined ? null : hintId, failed ? errorId : null, counter === undefined ? null : countId].filter(part => part !== null).join(" ");
  return <div data-testid={testId} className="x78zum5 xdt5ytf xskm3c9 xeuugli">
      <label htmlFor={control} {...stylex.props(textStyle.label, styles.label)}>
        {label}
        {optional && <span className="x1lm7cez x1pd3egz"> (optional)</span>}
      </label>
      {hint !== undefined && <p id={hintId} {...stylex.props(textStyle.caption, styles.hint)}>
          {hint}
        </p>}
      {children({
      id: control,
      "aria-describedby": describedBy === "" ? undefined : describedBy,
      "aria-invalid": failed || undefined,
      "aria-required": !optional || undefined
    })}
      {failed && <p id={errorId} role="alert" {...stylex.props(textStyle.label, styles.error)}>
          <WarningIcon />
          <span>{error}</span>
        </p>}
      {counter !== undefined && <p id={countId} {...stylex.props(textStyle.caption, styles.count, counter.length > counter.max && styles.over)}>
          {`${String(counter.length)} of ${String(counter.max)}`}
        </p>}
    </div>;
}